<script>
import { useMenuStore } from "@/store/menu";
import { useProductsStore } from "@/store/products";
import { useContextStore } from "@/store/context";
import { useMagasinStore } from "@/store/magasin";
import Breadcrumbs from "@/components/molecules/breadcrumbs/default";
import Button from "@/components/atoms/button";
import Card from "@/components/molecules/card";
import Carousel from "@/components/organisms/carousel";
import CarouselSlide from "@/components/organisms/carousel/carousel-slide";
import Container from "@/components/atoms/container";
import Divider from "@/components/atoms/divider/index";
import LazyImage from "@/components/atoms/lazy-image";
import LinkTo from "@/components/atoms/link-to";
import Video from "@/components/atoms/video";
import Titre from "@/components/atoms/titre";
import { mapState } from "pinia";
import productListApiDrive from "@/services/http/api-drive/magasin/product-list";
import { pushProps, usePush } from "@/components/pages/mixins/push-mixin";
import ItemContainer from "@/containers/panier/item";
import { usePageMarquesScroll } from "@/components/pages/mixins/marques-scroll.mixin.js";
import HtmlParser from "@/components/organisms/cms-container/html-parser";
import MarquesPageCodes from "@/utils/page-marques-codes";

export default {
  name: "MarquesLanding",
  components: {
    Breadcrumbs,
    Button,
    Card,
    Carousel,
    CarouselSlide,
    Container,
    Divider,
    LazyImage,
    LinkTo,
    Video,
    Titre,
    ItemContainer,
    HtmlParser
  },
  props: {
    ...pushProps()
  },
  setup(props) {
    const { onIntersect } = usePageMarquesScroll();
    const { getPushData, getPushList, getPushTitle } = usePush(props.pushZones);
    return { onIntersect, getPushData, getPushList, getPushTitle };
  },
  data() {
    return {
      autresMarquesCora: [],
      marquesPageCodes: MarquesPageCodes
    };
  },
  computed: {
    ...mapState(useContextStore, ["layout"]),
    ...mapState(useProductsStore, ["attributes", "breadcrumbs", "categoryId"]),
    ...mapState(useMenuStore, ["categories"]),
    ...mapState(useMagasinStore, { isNational: "isNational", magasinId: "id" }),
    productListItem() {
      return defineAsyncComponent(
        lazyComponents[
          `/components/organisms/product-list/product-list-item-grid/${this.layout}.vue`
        ]
      );
    },
    column() {
      return this.attributes.layout_type === "3ColonnesMarque" ? 3 : 2;
    },
    breadcrumb() {
      return this.breadcrumbs.map((item) => ({
        text: item.title,
        route: item.slug
      }));
    },
    marqueGamme() {
      return this.getPushList(this.marquesPageCodes.LANDING_MARQUE_HAUT);
    },
    marqueEngagement() {
      return this.getPushList(this.marquesPageCodes.LANDING_MARQUE_BAS);
    },
    marqueProduit() {
      return this.getPushData(this.marquesPageCodes.LANDING_MARQUE_PRODUIT);
    }
  },
  methods: {
    async getAutresMarquesCora() {
      const params = {
        shopId: this.magasinId,
        categoryId: useRuntimeConfig().public.COIN_DES_MARQUES_ID_TOUTES_MARQUES
      };
      const response = await this.$apiDrive
        .use(productListApiDrive)
        .products(params);
      return response.data.data.attributes.category.data.attributes.children.data.filter(
        (marque) => {
          return (
            marque.id !== this.categoryId && marque.attributes.is_marque_cora
          );
        }
      );
    }
  },
  async beforeMount() {
    if (this.attributes.is_marque_cora) {
      try {
        this.autresMarquesCora = await this.getAutresMarquesCora();
      } catch (e) {}
    }
  }
};
</script>
<template>
  <div
    :class="column && `c-marque-landing--column${column}`"
    class="c-marque-landing u-bgcolor-white"
  >
    <Container size="sm">
      <Breadcrumbs
        :breadcrumbs="breadcrumb"
        :divider="false"
      />
      <div class="c-marque-landing__cover">
        <LazyImage
          :src="attributes.image_bandeau"
          class="c-marque-landing__img"
        />
        <div class="c-marque-landing__logo">
          <LazyImage :src="attributes.category.data.attributes.visuel" />
        </div>
      </div>
      <Container size="xs">
        <div class="c-marque-landing__description u-pb-lg20">
          <Titre
            tag="h1"
            variant="primary"
            size="xlg"
            class="u-text-center@sm u-mb-xs u-mb-lg20@sm"
          >
            {{ attributes.title }}
          </Titre>
          <p
            v-if="attributes.description_longue"
            class="u-font-15 u-font-400 u-mt-lg10"
          >
            {{ attributes.description_longue }}
          </p>
          <Video
            v-if="attributes.url_video"
            :src="attributes.url_video"
            class="u-pt-lg20"
          />
        </div>
      </Container>
      <Divider
        v-if="marqueGamme.length > 0 || getPushData(marquesPageCodes.LANDING_MARQUE_LIBRE).html_content"
        dotted
        class="u-m-none"
      />
      <HtmlParser
        class="c-cms-container__content u-pt-sm"
        :content="getPushData(marquesPageCodes.LANDING_MARQUE_LIBRE).html_content"
      />
      <template v-if="marqueGamme.length > 0">
        <Titre
          tag="h2"
          variant="primary"
          size="md"
          class="u-text-center@sm u-py-md u-py-lg@sm"
        >
          {{ getPushTitle(marquesPageCodes.LANDING_MARQUE_HAUT) }}
        </Titre>
        <div class="c-marque-landing__block-wrapper u-pb-lg20">
          <template
            v-for="(gamme, index) in marqueGamme"
            :key="gamme.id"
          >
            <Card
              v-if="index < 9"
              class="u-full-width u-full-height"
            >
              <div class="u-flex u-flex-column u-full-height">
                <LazyImage
                  :src="gamme.image"
                  class="c-marque-landing__block-img u-mb-sm"
                />
                <div class="u-flex u-flex-column u-flex-grow">
                  <div class="u-flex-grow u-mb-xs">
                    <p class="u-font-primary u-font-16 u-font-700 u-mb-xs">
                      {{ gamme.front_label }}
                    </p>
                    <p class="u-font-primary u-font-15 u-font-400">
                      {{ gamme.description }}
                    </p>
                  </div>
                  <LinkTo
                    v-if="gamme.lien_cta"
                    :href="gamme.lien_cta.startsWith('http') ? gamme.lien_cta : null"
                    :to="gamme.lien_cta.startsWith('http') ? null : gamme.lien_cta"
                    variant="primary"
                    hover-color="primary-light"
                    class="u-font-15 u-font-400"
                    :target="gamme.lien_cta.startsWith('http') ? '_blank' : null"
                  >
                    {{ gamme.libelle_cta }}
                  </LinkTo>
                </div>
              </div>
            </Card>
          </template>
        </div>
      </template>
      <template v-if="marqueEngagement.length > 0">
        <Divider
          dotted
          class="u-m-none"
        />
        <Titre
          tag="h2"
          variant="primary"
          size="md"
          class="u-text-center@sm u-py-md u-py-lg@sm"
        >
          {{ getPushTitle(marquesPageCodes.LANDING_MARQUE_BAS) }}
        </Titre>
        <div class="c-marque-landing__block-wrapper u-pb-lg20">
          <template
            v-for="(engagement, index) in marqueEngagement"
            :key="engagement.id"
          >
            <Card
              v-if="index < 9"
              class="u-full-width u-full-height"
            >
              <div class="u-flex u-flex-column u-full-height">
                <LazyImage
                  :src="engagement.image"
                  class="c-marque-landing__block-img u-mb-sm"
                />
                <div class="u-flex u-flex-column u-flex-grow">
                  <div class="u-flex-grow u-mb-xs">
                    <p class="u-font-primary u-font-16 u-font-700 u-mb-xs">
                      {{ engagement.front_label }}
                    </p>
                    <p class="u-font-primary u-font-15 u-font-400">
                      {{ engagement.description }}
                    </p>
                  </div>
                  <LinkTo
                    v-if="engagement.lien_cta"
                    variant="primary"
                    hover-color="primary-light"
                    class="u-font-15 u-font-400"
                    :href="engagement.lien_cta.startsWith('http') ? engagement.lien_cta : null"
                    :to="engagement.lien_cta.startsWith('http') ? null : engagement.lien_cta"
                    :target="engagement.lien_cta.startsWith('http') ? '_blank' : null"
                  >
                    {{ engagement.libelle_cta }}
                  </LinkTo>
                </div>
              </div>
            </Card>
          </template>
        </div>
      </template>
    </Container>
    <div
      v-if="marqueProduit.products"
      id="decouvrezLesProduits"
      v-intersect="onIntersect"
      class="u-px-sm u-pt-lg u-pb-md u-bgcolor-bg"
    >
      <Titre
        tag="h2"
        variant="primary"
        size="md"
        class="u-text-center u-pb-md u-pb-lg@sm"
      >
        {{ marqueProduit.titre }}
      </Titre>
      <Carousel
        :elements-per-slide="'auto'"
        :space-between="20"
        :navigation-arrows="true"
        :free-mode="true"
        :scroll-bar="true"
      >
        <CarouselSlide
          v-for="(produit, i) in marqueProduit.products"
          :key="i"
          product-slide
        >
          <ItemContainer
            :component="productListItem"
            :article="produit"
            :index="i"
            is-carousel-product
          />
        </CarouselSlide>
      </Carousel>
      <div class="u-text-center u-pt-20 u-pb-sm">
        <Button
          size="md"
          radius="full"
          variant="primary"
          :to="marqueProduit.lien_cta"
        >
          Voir tous les produits {{ attributes.title }}
        </Button>
      </div>
    </div>
    <Container size="sm">
      <div
        v-if="attributes.is_marque_cora && autresMarquesCora.length > 0"
        class="u-pt-lg20"
      >
        <Divider
          dotted
          class="u-m-none"
        />
        <p class=" u-font-secondary u-font-24 u-font-700 u-color-primary u-py-md">
          Découvrez les autres marques Cora
        </p>
        <div class="c-marque-landing__marques-wrapper">
          <Card
            v-for="marque in autresMarquesCora"
            :key="marque.id"
            radius="md"
            elevation="sm"
            border-color="grey-5"
            class="c-marque-landing__card u-p-sm"
          >
            <div class="u-flex u-flex-column u-flex-align-center u-full-height">
              <LazyImage :src="marque.attributes.visuel" />
              <div class="u-pt-sm u-font-primary u-flex u-flex-column u-flex-grow u-full-width">
                <div class="u-font-14 u-flex-grow">
                  <p class="u-font-16 u-font-700 u-mb-xs">
                    {{ marque.attributes.title }}
                  </p>
                  <p class="u-font-400 u-mb-xs">
                    {{ marque.attributes.description_courte }}
                  </p>
                </div>
                <LinkTo
                  variant="primary"
                  hover-color="primary-light"
                  class="u-font-14 u-font-400"
                  :to="marque.attributes.slug"
                >
                  En savoir plus
                </LinkTo>
              </div>
            </div>
          </Card>
        </div>
      </div>
      <HtmlParser
        v-if="attributes.description"
        :content="attributes.description"
        class="u-pt-lg30 u-pb-md"
      />
    </Container>
  </div>
</template>
<style lang="scss" scoped>
@import "@/assets/styles/components/page-marque";
@import "@/assets/styles/globals/cms.scss";
</style>
